import React from 'react';
import { Button, Chip, Grid, Typography } from '@mui/material';
import { Category } from '@mui/icons-material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTranslation } from 'react-i18next';
import { ExtraItemLineData, ExtraPaymentLineData, ExtraPaymentMode, OrderData } from '../../../api';
import { PAYMENT_LINE_CARD_COLOR, PAYMENT_LINE_CASH_COLOR } from '../../../common/paymentLineColors';
import TimeUtils from '../../../utils/TimeUtils';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import { useUsers } from '../../../session/swr/useUsers';
import { getUserFullNameFromUserId } from '../../components/UsersMultiSelect';
import { BODY_WEIGHT, FONT_SIZE, MUTED_TEXT } from '../agendaStyles';
import { isManuallySeated } from '../../utils/orderSeating';
import { OrderPlacement, OrderPlacementType } from '../utils/orderAreas';

type AgendaOrderDetailsProps = {
  order: OrderData;
  placement: OrderPlacement;
  extraItems: ExtraItemLineData[];
  itemsTotal: number;
  paymentLines: ExtraPaymentLineData[];
  customerComment: string | undefined;
  /** Already formatted; null when not today or not checked in. */
  elapsedText: string | null;
  /** Chips that did not fit on the card's first line. */
  overflowChips: { key: string; node: React.ReactNode }[];
  projectSelectionPending: boolean;
  timeFormat: string;
  timezone: string;
  formatAmount: (amount: number) => React.ReactNode;
};

const stop = (event: React.MouseEvent) => event.stopPropagation();

const AmountRow = ({
  label,
  amount,
  bold = false,
  color,
}: {
  label: string;
  amount: React.ReactNode;
  bold?: boolean;
  color?: string;
}) => (
  <Grid container justifyContent="space-between" gap={1} wrap="nowrap">
    <Typography sx={{ fontSize: FONT_SIZE.body, fontWeight: bold ? 700 : BODY_WEIGHT }}>{label}</Typography>
    <Typography dir="ltr" sx={{ fontSize: FONT_SIZE.body, fontWeight: bold ? 700 : BODY_WEIGHT, color }}>
      {amount}
    </Typography>
  </Grid>
);

/**
 * What a card shows when unfolded: the items and money, payments, both notes, the check-in time
 * and the move-table shortcut. Balance due is displayed as the backend returns it, never recomputed.
 */
export const AgendaOrderDetails = ({
  order,
  placement,
  extraItems,
  itemsTotal,
  paymentLines,
  customerComment,
  elapsedText,
  overflowChips,
  projectSelectionPending,
  timeFormat,
  timezone,
  formatAmount,
}: AgendaOrderDetailsProps) => {
  const { t } = useTranslation();
  const { openEditOrderDrawer } = useCalendar();
  const { users } = useUsers();
  const balanceDue = order.balance_due ?? 0;

  return (
    <Grid
      container
      direction="column"
      gap={1}
      wrap="nowrap"
      sx={{
        paddingInline: 1.5,
        paddingBottom: 1.5,
        paddingTop: 1,
        fontSize: FONT_SIZE.body,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}>
      <Grid container direction="column" gap={0.5} wrap="nowrap">
        <Typography sx={{ fontSize: FONT_SIZE.body, fontWeight: 700 }}>
          {t('calendar.areas_view.items_title')}
        </Typography>
        {extraItems.length > 0 ? (
          <>
            {extraItems.map((line) => (
              <AmountRow
                key={line.id}
                label={`${line.quantity} × ${line.label}`}
                amount={formatAmount(line.unit_price * line.quantity)}
              />
            ))}
            <AmountRow label={t('calendar.areas_view.items_total')} amount={formatAmount(itemsTotal)} />
            <AmountRow
              label={t('calendar.areas_view.prepaid')}
              amount={formatAmount(order.deposit_price ?? order.price)}
            />
            <AmountRow
              label={t('calendar.areas_view.balance_due')}
              amount={formatAmount(balanceDue)}
              bold
              color={balanceDue > 0 ? 'error.main' : undefined}
            />
          </>
        ) : (
          <Grid container alignItems="center" gap={1} wrap="nowrap">
            <Typography sx={{ fontWeight: BODY_WEIGHT, fontSize: FONT_SIZE.body, color: MUTED_TEXT }}>
              {t('calendar.areas_view.no_items_yet')}
            </Typography>
            {order.checkin && (
              <Button
                size="small"
                onClick={(event) => {
                  stop(event);
                  openEditOrderDrawer({ order, focusExtraItems: true, openCatalog: true });
                }}>
                {t('calendar.areas_view.add_items')}
              </Button>
            )}
          </Grid>
        )}
      </Grid>

      {order.balance_closed_at && (
        <Typography sx={{ fontWeight: BODY_WEIGHT, fontSize: FONT_SIZE.body, color: MUTED_TEXT }}>
          {t('calendar.areas_view.closed_with_note_line', { note: order.balance_closed_note })}
          {' · '}
          {t('calendar.areas_view.closed_by', {
            name: getUserFullNameFromUserId(users, order.balance_closed_by ?? ''),
            time: TimeUtils.getLocalTime(order.balance_closed_at, timezone).format(timeFormat),
          })}
        </Typography>
      )}

      {paymentLines.length > 0 && (
        <Grid container direction="column" gap={0.5} wrap="nowrap">
          <Typography sx={{ fontSize: FONT_SIZE.body, fontWeight: 700 }}>
            {t('calendar.areas_view.payments_title')}
          </Typography>
          {paymentLines.map((line, index) => (
            <Grid
              key={line.id ?? `${line.title}-${index}`}
              container
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              wrap="nowrap">
              <Grid container alignItems="center" gap={0.5} wrap="nowrap" sx={{ minWidth: 0, width: 'auto' }}>
                {/* A provider transaction id means a real charge (POS or saved card); otherwise staff recorded it. */}
                {line.payment_mode === ExtraPaymentMode.POS_TERMINAL ? (
                  <PointOfSaleIcon
                    titleAccess={t('calendar.edit_order.order_details.paidAtPosTerminal')}
                    sx={{ fontSize: '16px', color: PAYMENT_LINE_CARD_COLOR, flexShrink: 0 }}
                  />
                ) : line.provider_transaction_id ? (
                  <CreditCardIcon
                    titleAccess={t('calendar.edit_order.order_details.chargedToSavedCard')}
                    sx={{ fontSize: '16px', color: PAYMENT_LINE_CARD_COLOR, flexShrink: 0 }}
                  />
                ) : (
                  <LocalAtmIcon
                    titleAccess={
                      line.payment_method_label
                        ? t('calendar.edit_order.order_details.recordedManuallyWith', {
                            method: line.payment_method_label,
                          })
                        : t('calendar.edit_order.order_details.recordedManually')
                    }
                    sx={{ fontSize: '16px', color: PAYMENT_LINE_CASH_COLOR, flexShrink: 0 }}
                  />
                )}
                <Typography
                  sx={{
                    fontWeight: BODY_WEIGHT,
                    fontSize: FONT_SIZE.body,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {line.title}
                  {line.payment_method_label && (
                    <Typography component="span" color="text.secondary" sx={{ fontSize: 'inherit' }}>
                      {` · ${line.payment_method_label}`}
                    </Typography>
                  )}
                </Typography>
              </Grid>
              <Typography dir="ltr" sx={{ fontWeight: BODY_WEIGHT, fontSize: FONT_SIZE.body, flexShrink: 0 }}>
                {formatAmount(line.price)}
              </Typography>
            </Grid>
          ))}
        </Grid>
      )}

      {order.comment && (
        <Grid container direction="column" wrap="nowrap">
          <Typography sx={{ fontSize: FONT_SIZE.body, fontWeight: 700 }}>
            {t('calendar.areas_view.staff_comment')}
          </Typography>
          <Typography sx={{ fontWeight: BODY_WEIGHT, fontSize: FONT_SIZE.body, whiteSpace: 'pre-wrap' }}>
            {order.comment}
          </Typography>
        </Grid>
      )}
      {customerComment && (
        <Grid container direction="column" wrap="nowrap">
          <Typography sx={{ fontSize: FONT_SIZE.body, fontWeight: 700 }}>
            {t('calendar.areas_view.customer_comment')}
          </Typography>
          <Typography sx={{ fontWeight: BODY_WEIGHT, fontSize: FONT_SIZE.body, whiteSpace: 'pre-wrap' }}>
            {customerComment}
          </Typography>
        </Grid>
      )}

      {order.checkin && (
        <Typography sx={{ fontWeight: BODY_WEIGHT, fontSize: FONT_SIZE.body }}>
          {/* The sentence ends with the time in both languages, so the time can trail in its own ltr span. */}
          {t('calendar.areas_view.checked_in_at', { time: '' })}
          <span dir="ltr">{TimeUtils.getLocalTime(order.checkin.created_at, timezone).format(timeFormat)}</span>
          {elapsedText !== null && ` · ${t('calendar.areas_view.here_for', { duration: elapsedText })}`}
        </Typography>
      )}

      {(overflowChips.length > 0 || isManuallySeated(order) || projectSelectionPending) && (
        <Grid container alignItems="center" gap={0.5}>
          {overflowChips.map((chip) => (
            <React.Fragment key={chip.key}>{chip.node}</React.Fragment>
          ))}
          {isManuallySeated(order) && (
            <Chip size="small" variant="outlined" label={t('calendar.areas_view.manual_assignment')} />
          )}
          {projectSelectionPending && (
            <Chip
              size="small"
              icon={<Category sx={{ fontSize: '14px' }} />}
              label={t('calendar.areas_view.project_not_chosen')}
              sx={{
                color: 'mb.clayInk',
                backgroundColor: 'mb.claySoft',
                fontWeight: 700,
                '& .MuiChip-icon': { color: 'mb.clayInk' },
              }}
            />
          )}
        </Grid>
      )}

      {placement.type !== OrderPlacementType.NON_SCHEDULER && (
        <Grid container>
          <Button
            size="small"
            startIcon={<SwapHorizIcon />}
            onClick={(event) => {
              stop(event);
              openEditOrderDrawer({ order, focusResources: true });
            }}>
            {t('calendar.areas_view.move_table')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};
